/*
 * Theme behaviour — drawers, cart AJAX, variant selection, gallery,
 * sticky add-to-cart, carousels and scroll reveals.
 *
 * Principles: minimal JS, progressive enhancement, graceful degradation.
 * Custom elements re-bind automatically on theme-editor section reloads.
 */
(function () {
  'use strict';

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function finishImageLoading(image) {
    if (image instanceof HTMLImageElement) image.dataset.imageLoaded = '';
  }

  document.addEventListener('load', function (event) {
    finishImageLoading(event.target);
  }, true);
  document.addEventListener('error', function (event) {
    finishImageLoading(event.target);
  }, true);
  document.querySelectorAll('img').forEach(function (image) {
    if (image.complete) finishImageLoading(image);
  });

  // Move the children of a parsed, trusted node into a live element (no innerHTML).
  function adoptChildren(target, sourceNode) {
    var imported = document.importNode(sourceNode, true);
    target.replaceChildren.apply(target, Array.prototype.slice.call(imported.childNodes));
  }

  // Trailing-edge debounce.
  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(ctx, args);
      }, wait);
    };
  }

  var fastAverageColorPromise;
  function loadFastAverageColor() {
    if (!fastAverageColorPromise) {
      fastAverageColorPromise = import('https://cdn.jsdelivr.net/npm/fast-average-color@9.5.2/dist/index.esm.js');
    }
    return fastAverageColorPromise;
  }

  function getMediaSampleOptions(media, target) {
    var naturalWidth = media.naturalWidth || media.videoWidth;
    var naturalHeight = media.naturalHeight || media.videoHeight;
    if (!naturalWidth || !naturalHeight) return null;

    var mediaRect = media.getBoundingClientRect();
    var targetRect = target.getBoundingClientRect();
    var leftEdge = Math.max(mediaRect.left, targetRect.left);
    var topEdge = Math.max(mediaRect.top, targetRect.top);
    var rightEdge = Math.min(mediaRect.right, targetRect.right);
    var bottomEdge = Math.min(mediaRect.bottom, targetRect.bottom);
    if (rightEdge <= leftEdge || bottomEdge <= topEdge) return null;

    var styles = getComputedStyle(media);
    var scaleX = mediaRect.width / naturalWidth;
    var scaleY = mediaRect.height / naturalHeight;
    if (styles.objectFit === 'cover') scaleX = scaleY = Math.max(scaleX, scaleY);
    if (styles.objectFit === 'contain') scaleX = scaleY = Math.min(scaleX, scaleY);

    var renderedWidth = naturalWidth * scaleX;
    var renderedHeight = naturalHeight * scaleY;
    var position = styles.objectPosition.split(' ');
    var positionX = parseFloat(position[0]) / 100;
    var positionY = parseFloat(position[1]) / 100;
    if (!Number.isFinite(positionX)) positionX = 0.5;
    if (!Number.isFinite(positionY)) positionY = 0.5;

    var offsetX = (mediaRect.width - renderedWidth) * positionX;
    var offsetY = (mediaRect.height - renderedHeight) * positionY;
    var left = Math.max(0, (leftEdge - mediaRect.left - offsetX) / scaleX);
    var top = Math.max(0, (topEdge - mediaRect.top - offsetY) / scaleY);
    var width = Math.min((rightEdge - leftEdge) / scaleX, naturalWidth - left);
    var height = Math.min((bottomEdge - topEdge) / scaleY, naturalHeight - top);
    if (width <= 0 || height <= 0) return null;

    return {
      left: Math.floor(left),
      top: Math.floor(top),
      width: Math.ceil(width),
      height: Math.ceil(height),
      mode: 'speed',
      silent: true,
    };
  }

  /* ----------------------------------------------------------------------
   * Drawers (cart + nav) — shared custom element behaviour
   * -------------------------------------------------------------------- */
  function lockScroll() {
    document.documentElement.classList.add('overflow-locked');
  }
  function maybeUnlockScroll() {
    if (!document.querySelector('[data-drawer][data-open]')) {
      document.documentElement.classList.remove('overflow-locked');
    }
  }

  class Drawer extends HTMLElement {
    connectedCallback() {
      this._onKey = (e) => {
        if (e.key === 'Escape') this.close();
      };
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-drawer-overlay]') || e.target.closest('[data-drawer-close]')) {
          this.close();
        }
      });
    }
    open() {
      this.setAttribute('data-open', '');
      this._setControlsExpanded(true);
      lockScroll();
      document.addEventListener('keydown', this._onKey);
      var focusTarget = this.querySelector('[data-drawer-close]');
      if (focusTarget) focusTarget.focus();
    }
    close() {
      this.removeAttribute('data-open');
      this._setControlsExpanded(false);
      document.removeEventListener('keydown', this._onKey);
      maybeUnlockScroll();
    }
    // Reflect open/closed state on any trigger that controls this drawer.
    _setControlsExpanded(expanded) {
      if (!this.id) return;
      document
        .querySelectorAll('[aria-controls="' + this.id + '"]')
        .forEach((el) => el.setAttribute('aria-expanded', expanded ? 'true' : 'false'));
    }
  }

  if (!customElements.get('cart-drawer'))
    customElements.define(
      'cart-drawer',
      class extends Drawer {
        open() {
          super.open();
          loadCartRecommendations();
        }
      }
    );
  if (!customElements.get('nav-drawer')) customElements.define('nav-drawer', class extends Drawer {});

  /* ----------------------------------------------------------------------
   * Filter drawer — AJAX filter/sort for the PLP (collection.liquid)
   *
   * Extends the shared Drawer base. Intercepts every filter control change
   * inside the panel, builds a new URL from the native Shopify filter URL
   * attributes, fetches the section partial, and swaps the relevant DOM
   * regions without a full page reload. history.pushState keeps the URL in
   * sync so filtered results are bookmarkable and back/forward-safe.
   * -------------------------------------------------------------------- */
  class FilterDrawer extends Drawer {
    connectedCallback() {
      super.connectedCallback();
      this.sectionId = this._productList() && this._productList().dataset.sectionId;

      // Checkbox / boolean filter ticks
      this.addEventListener('change', (e) => {
        var check = e.target.closest('[data-filter-check]');
        if (check) {
          e.preventDefault();
          this._applyUrl(check.dataset.url);
          return;
        }
        // Sort-by select
        var sortSel = e.target.closest('[data-filter-sort]');
        if (sortSel) {
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortSel.value);
          url.searchParams.delete('page');
          this._applyUrl(url.toString());
        }
      });

      // Price range — debounced on input
      this._priceTimer = null;
      this.addEventListener('input', (e) => {
        var priceWrap = e.target.closest('[data-filter-price]');
        if (!priceWrap) return;
        clearTimeout(this._priceTimer);
        this._priceTimer = setTimeout(() => {
          var url = new URL(window.location.href);
          var minInput = priceWrap.querySelector('[data-filter-price-min]');
          var maxInput = priceWrap.querySelector('[data-filter-price-max]');
          var paramMin = priceWrap.dataset.paramMin;
          var paramMax = priceWrap.dataset.paramMax;
          if (minInput && minInput.value !== '') {
            url.searchParams.set(paramMin, minInput.value * 100);
          } else {
            url.searchParams.delete(paramMin);
          }
          if (maxInput && maxInput.value !== '') {
            url.searchParams.set(paramMax, maxInput.value * 100);
          } else {
            url.searchParams.delete(paramMax);
          }
          url.searchParams.delete('page');
          this._applyUrl(url.toString());
        }, 600);
      });

      // Chip remove links and clear-one / clear-all links inside the drawer
      this.addEventListener('click', (e) => {
        var clearOne = e.target.closest('[data-filter-clear-one]');
        var clearAll = e.target.closest('[data-filter-clear-all]');
        if (clearOne || clearAll) {
          e.preventDefault();
          this._applyUrl((clearOne || clearAll).getAttribute('href'));
        }
      });
    }

    // Also intercept chip-remove links rendered outside the drawer (in the toolbar chips row)
    _bindChipRemovals() {
      document.addEventListener('click', (e) => {
        var chip = e.target.closest('[data-filter-chip-remove]');
        if (!chip) return;
        e.preventDefault();
        this._applyUrl(chip.getAttribute('href'));
      }, { once: false });
    }

    _productList() {
      return document.querySelector('product-list');
    }

    async _applyUrl(targetUrl) {
      if (!targetUrl || !this.sectionId) return;

      var url = new URL(targetUrl, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);
      url.searchParams.delete('page');

      // Show loading state
      var toolbar = document.querySelector('[data-filter-toolbar]');
      if (toolbar) toolbar.setAttribute('aria-busy', 'true');

      try {
        var res = await fetch(url.toString());
        var text = await res.text();
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var section = doc.querySelector('#shopify-section-' + this.sectionId);
        if (!section) section = doc.body;

        this._swap(section, '[data-product-grid]', true);
        this._swap(section, '[data-product-list-footer]');
        this._swap(section, '[data-filter-toolbar]');
        this._swap(section, '[data-filter-chips]');
        this._swap(section, '[data-filter-body]');

        // Update address bar
        var cleanUrl = new URL(targetUrl, window.location.origin);
        cleanUrl.searchParams.delete('section_id');
        history.pushState(history.state, '', cleanUrl);

        // Re-init infinite scroll on the new product list state
        var pl = this._productList();
        if (pl && typeof pl.reinit === 'function') pl.reinit();
      } catch (err) {
        // Silently fall back; page is still usable
      } finally {
        if (toolbar) toolbar.removeAttribute('aria-busy');
      }
    }

    _swap(sourceDoc, selector, scrollTop) {
      var fresh = sourceDoc.querySelector(selector);
      var live = document.querySelector(selector);
      if (fresh && live) {
        adoptChildren(live, fresh);
        // Copy attributes that change (class, hidden, aria-busy etc.)
        Array.prototype.forEach.call(fresh.attributes, (attr) => {
          live.setAttribute(attr.name, attr.value);
        });
        if (scrollTop) live.scrollTop = 0;
      }
    }
  }

  if (!customElements.get('filter-drawer')) customElements.define('filter-drawer', FilterDrawer);

  // Wire up chip removals from outside the drawer
  (function () {
    document.addEventListener('click', (e) => {
      var chip = e.target.closest('[data-filter-chip-remove]');
      if (!chip) return;
      e.preventDefault();
      var drawer = document.querySelector('filter-drawer');
      if (drawer && typeof drawer._applyUrl === 'function') {
        drawer._applyUrl(chip.getAttribute('href'));
      }
    });
  })();

  // Close every open drawer except an optional one (prevents stacking).
  function closeOpenDrawers(exceptId) {
    document.querySelectorAll('[data-drawer][data-open]').forEach((d) => {
      if (d.id !== exceptId && typeof d.close === 'function') d.close();
    });
  }

  function openDrawerById(id) {
    var drawer = document.getElementById(id);
    if (!drawer || !drawer.open) return;
    closeOpenDrawers(id);
    drawer.open();
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-toggle]')) {
      e.preventDefault();
      openDrawerById('cart-drawer');
      return;
    }
    if (e.target.closest('[data-nav-toggle]')) {
      e.preventDefault();
      openDrawerById('nav-drawer');
      return;
    }
    if (e.target.closest('[data-filter-toggle]')) {
      e.preventDefault();
      openDrawerById('filter-drawer');
      return;
    }
    if (e.target.closest('[data-search-toggle]')) {
      e.preventDefault();
      openDrawerById('search-drawer');
    }
  });

  /* ----------------------------------------------------------------------
   * Search drawer — predictive results via the Section Rendering API
   * -------------------------------------------------------------------- */
  class SearchDrawer extends Drawer {
    connectedCallback() {
      super.connectedCallback();
      this.form = this.querySelector('[data-predictive-search]');
      this.input = this.querySelector('[data-predictive-input]');
      this.results = this.querySelector('[data-predictive-results]');
      this.skeleton = this.querySelector('[data-search-skeleton]');
      this.defaultState = this.querySelector('[data-search-default]');
      this.controller = null;
      if (this.input) {
        this.input.addEventListener('input', debounce(() => this.onInput(), 300));
      }
      // Suggestion chips fill the field and search in place rather than navigating.
      this.addEventListener('click', (e) => {
        var chip = e.target.closest('[data-search-term]');
        if (!chip || !this.input) return;
        e.preventDefault();
        this.input.value = chip.getAttribute('data-search-term');
        this.input.focus();
        this.onInput();
      });
    }
    open() {
      super.open();
      // Focus the field and re-sync the view to whatever the input holds.
      if (this.input) {
        requestAnimationFrame(() => this.input.focus());
        this.onInput();
      }
    }
    close() {
      super.close();
      if (this.controller) this.controller.abort();
      if (this.form) this.form.removeAttribute('data-loading');
    }
    // Toggle the visible body view: 'default' | 'loading' | 'results'.
    setView(view) {
      if (this.defaultState) this.defaultState.hidden = view !== 'default';
      if (this.skeleton) this.skeleton.hidden = view !== 'loading';
      if (this.results) {
        this.results.hidden = view !== 'results';
        this.results.toggleAttribute('aria-busy', view === 'loading');
      }
      if (this.form) this.form.toggleAttribute('data-loading', view === 'loading');
      if (this.input) this.input.setAttribute('aria-expanded', view === 'default' ? 'false' : 'true');
    }
    onInput() {
      var term = (this.input.value || '').trim();
      if (!term.length) {
        this.showDefault();
        return;
      }
      this.fetchResults(term);
    }
    showDefault() {
      this.setView('default');
      if (this.results) this.results.replaceChildren();
    }
    async fetchResults(term) {
      if (!this.results) return;
      this.setView('loading');

      var url =
        root +
        'search/suggest?q=' +
        encodeURIComponent(term) +
        '&resources[type]=product,collection&resources[limit]=6&section_id=predictive-search';

      try {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        var res = await fetch(url, { signal: this.controller.signal });
        var text = await res.text();
        var fresh = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search');
        if (fresh) adoptChildren(this.results, fresh);
        this.setView('results');
      } catch (err) {
        if (err.name === 'AbortError') return; // a newer query is already loading
        this.showDefault(); // network failure — fall back to discovery
      }
    }
  }
  if (!customElements.get('search-drawer')) customElements.define('search-drawer', SearchDrawer);

  function openCartDrawer() {
    var cart = document.getElementById('cart-drawer');
    if (cart && cart.open) cart.open();
  }

  /* ----------------------------------------------------------------------
   * Cart — add / change via AJAX + bundled section rendering
   * -------------------------------------------------------------------- */
  // Sections to re-render on every cart mutation: the drawer + header count
  // always, plus the /cart page section when the shopper is on it, so the page
  // and the drawer stay in sync from a single request.
  function cartSections() {
    var list = 'cart-drawer,header';
    var page = document.querySelector('[data-cart-page]');
    if (page) list += ',' + page.getAttribute('data-section-id');
    return list;
  }

  function renderCartSections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) {
      var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var fresh = parsed.querySelector('[data-cart-contents]');
      var live = document.querySelector('#cart-drawer [data-cart-contents]');
      if (fresh && live) {
        adoptChildren(live, fresh);
        initSwipers(live); // re-init the complimentary-samples carousel on the fresh markup
        checkStaleGifts(live);
      }
    }
    var page = document.querySelector('[data-cart-page]');
    if (page && sections[page.getAttribute('data-section-id')]) {
      var parsedPage = new DOMParser().parseFromString(sections[page.getAttribute('data-section-id')], 'text/html');
      var freshPage = parsedPage.querySelector('[data-cart-page-contents]');
      var livePage = page.querySelector('[data-cart-page-contents]');
      if (freshPage && livePage) {
        adoptChildren(livePage, freshPage);
        initSwipers(livePage);
        checkStaleGifts(livePage);
      }
    }
    if (sections['header']) {
      var ph = new DOMParser().parseFromString(sections['header'], 'text/html');
      var freshCount = ph.querySelector('[data-cart-count]');
      var liveCount = document.querySelector('header [data-cart-count]');
      if (freshCount && liveCount) liveCount.replaceWith(document.importNode(freshCount, true));
    }
    // Re-seed drawer recommendations when the cart's first item (or emptiness) may have changed.
    loadCartRecommendations();
  }

  /* ----------------------------------------------------------------------
   * Free gifts — auto-correct lines that no longer qualify: a tier fell back
   * below its threshold (shopper removed another item), a tier's selection
   * limit was exceeded, or a duplicate-add race merged into a line with
   * quantity above what the tier allows. `cart-gifts.liquid` computes the
   * corrected quantity for each affected line server-side (0 removes it,
   * >0 caps it) and exposes it via `data-stale-gift-updates` as
   * "key,quantity" pairs separated by "|" — line keys can contain commas
   * only never "|", so that's a safe pair delimiter. This applies those
   * updates in one request and re-renders so the swiper and progress
   * tracker stay in sync.
   * -------------------------------------------------------------------- */
  function parseStaleGiftUpdates(value) {
    var updates = {};
    value
      .split('|')
      .filter(Boolean)
      .forEach(function (pair) {
        var separatorIndex = pair.lastIndexOf(',');
        if (separatorIndex === -1) return;
        var key = pair.slice(0, separatorIndex);
        var quantity = parseInt(pair.slice(separatorIndex + 1), 10);
        if (key && Number.isInteger(quantity) && quantity >= 0) updates[key] = quantity;
      });
    return updates;
  }

  function checkStaleGifts(scope) {
    var updates = {};
    scope.querySelectorAll('[data-stale-gift-updates]').forEach(function (section) {
      Object.assign(updates, parseStaleGiftUpdates(section.getAttribute('data-stale-gift-updates')));
    });
    if (Object.keys(updates).length) removeStaleGifts(updates);
  }

  var staleGiftCleanupPending = false;

  async function removeStaleGifts(updates) {
    if (staleGiftCleanupPending) return;
    staleGiftCleanupPending = true;
    try {
      var res = await fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: updates,
          sections: cartSections(),
          sections_url: window.location.pathname
        })
      });
      var data = await res.json();
      if (res.ok) renderCartSections(data.sections);
    } catch (err) {
      /* network error — the next cart mutation's re-render will retry this check */
    } finally {
      staleGiftCleanupPending = false;
    }
  }

  /* ----------------------------------------------------------------------
   * Cart drawer recommendations — Shopify Product Recommendations API,
   * fetched on demand and shown as a scrollable list in the left panel.
   * -------------------------------------------------------------------- */
  var recSeedProductId = null;

  function loadCartRecommendations() {
    var panel = document.querySelector('#cart-drawer [data-cart-recs-panel]');
    var target = panel && panel.querySelector('[data-cart-recs]');
    if (!panel || !target) return;

    var seed = document.querySelector('#cart-drawer [data-cart-first-product]');
    var productId = seed && seed.getAttribute('data-cart-first-product');

    if (!productId) {
      // Empty cart — hide the panel and reset.
      panel.removeAttribute('data-show');
      target.replaceChildren();
      recSeedProductId = null;
      return;
    }
    if (productId === recSeedProductId) return; // already loaded for this seed
    recSeedProductId = productId;

    var url =
      root +
      'recommendations/products?section_id=cart-recommendations&intent=related&limit=10&product_id=' +
      encodeURIComponent(productId);

    fetch(url)
      .then((r) => r.text())
      .then((html) => {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var recommendationsList = parsed.querySelector('[data-cart-recs-list]');
        if (!recommendationsList) {
          panel.removeAttribute('data-show');
          target.replaceChildren();
          return;
        }
        target.replaceChildren(document.importNode(recommendationsList, true));
        panel.setAttribute('data-show', '');
      })
      .catch(() => {
        recSeedProductId = null; // allow a retry next open
      });
  }

  function setButtonLoading(button, loading) {
    if (!button) return;
    button.toggleAttribute('aria-busy', loading);
    button.disabled = loading;
  }

  async function addToCart(form) {
    var button = form.querySelector('[data-add-to-cart]');
    var errorEl = form.querySelector('[data-form-error]');
    var formData = new FormData(form);
    formData.append('sections', cartSections());
    formData.append('sections_url', window.location.pathname);

    setButtonLoading(button, true);
    if (errorEl) errorEl.textContent = '';

    try {
      var res = await fetch(root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      var data = await res.json();
      if (!res.ok) {
        if (errorEl) errorEl.textContent = data.description || data.message || '';
        return;
      }
      renderCartSections(data.sections);
      openCartDrawer();
    } catch (err) {
      form.submit(); // network failure — fall back to a full-page add
    } finally {
      setButtonLoading(button, false);
    }
  }

  async function changeLine(item, line, quantity) {
    if (item) item.setAttribute('data-loading', ''); // veil + spinner over the row
    try {
      var res = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: Number(line),
          quantity: quantity,
          sections: cartSections(),
          sections_url: window.location.pathname
        })
      });
      var data = await res.json();
      renderCartSections(data.sections); // replaces the row (and its loading state) with fresh markup
    } catch (err) {
      window.location.href = root + 'cart';
    } finally {
      // Only clears if the re-render didn't replace the node (e.g. sections missing).
      if (item && item.isConnected) item.removeAttribute('data-loading');
    }
  }

  document.addEventListener('submit', (e) => {
    var form = e.target.closest('form[data-product-form]');
    if (!form) return;
    e.preventDefault();
    addToCart(form);
  });

  document.addEventListener('click', (e) => {
    // Checkout: let the form submit, but show a spinner while the page navigates.
    var checkout = e.target.closest('[data-checkout]');
    if (checkout) {
      checkout.setAttribute('aria-busy', '');
      return;
    }

    var item = e.target.closest('[data-cart-item]');
    if (!item || item.hasAttribute('data-loading')) return; // ignore repeat clicks mid-request
    var line = item.dataset.line;
    var qtyEl = item.querySelector('[data-cart-qty]');
    var qty = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
    if (e.target.closest('[data-cart-plus]')) changeLine(item, line, qty + 1);
    else if (e.target.closest('[data-cart-minus]')) changeLine(item, line, Math.max(0, qty - 1));
    else if (e.target.closest('[data-cart-remove]')) changeLine(item, line, 0);
  });

  // Resolve the clicked pair against the current cart before removing its box.
  // Liquid keys can change after discounts, while the pair properties identify
  // which box the shopper clicked even when several boxes share a variant.
  async function removeGiftBox(button) {
    button.setAttribute('aria-busy', 'true');
    var errorEl = button.closest('li').querySelector('[data-gift-box-remove-error]');
    if (errorEl) errorEl.textContent = '';
    try {
      var cartRes = await fetch(root + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!cartRes.ok) throw new Error('Could not read cart');
      var cartData = await cartRes.json();
      var item1 = button.getAttribute('data-gift-box-item-1');
      var item2 = button.getAttribute('data-gift-box-item-2');
      var line = cartData.items.findIndex(function (item) {
        var properties = item.properties || {};
        return properties._gift_box &&
          String(properties._gift_box_item_1) === item1 &&
          String(properties._gift_box_item_2) === item2;
      });
      if (line < 0) throw new Error('Gift box not found');

      var res = await fetch(root + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line: line + 1,
          quantity: 0,
          sections: cartSections(),
          sections_url: window.location.pathname
        })
      });
      if (!res.ok) throw new Error('Could not remove gift box');
      var data = await res.json();
      renderCartSections(data.sections);
    } catch (err) {
      if (errorEl) errorEl.textContent = errorEl.dataset.errorLabel;
      console.error('Could not remove gift box:', err);
    } finally {
      if (button.isConnected) button.removeAttribute('aria-busy');
    }
  }

  document.addEventListener('click', (e) => {
    var remove = e.target.closest('[data-gift-box-remove]');
    if (!remove || remove.hasAttribute('aria-busy')) return;
    removeGiftBox(remove);
  });

  /* ----------------------------------------------------------------------
   * Complimentary samples — toggle a sample into/out of the cart. Adds carry a
   * private `_sample` line-item property so the chooser (and cart list) can tell
   * samples apart; removes go by line key. State is recomputed server-side on
   * the bundled re-render, so there's nothing to update optimistically here.
   * -------------------------------------------------------------------- */
  var giftMutationPending = false;

  async function clearOtherGiftChoice(button, type) {
    var chooser = button.closest('[data-cart-gifts]');
    var updates = {};
    if (chooser) {
      chooser.querySelectorAll('[data-' + type + '-remove]').forEach(function (selected) {
        updates[selected.dataset.lineKey] = 0;
      });
    }
    if (!Object.keys(updates).length) return null;
    var res = await fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates, sections: cartSections(), sections_url: window.location.pathname })
    });
    var data = await res.json();
    if (!res.ok) throw new Error('Could not switch free gift');
    return data.sections;
  }

  async function toggleSample(button) {
    if (giftMutationPending) return;
    giftMutationPending = true;
    button.setAttribute('data-loading', '');
    var switchedSections = null;
    try {
      var res;
      if (button.hasAttribute('data-sample-remove')) {
        res = await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: button.dataset.lineKey,
            quantity: 0,
            sections: cartSections(),
            sections_url: window.location.pathname
          })
        });
      } else {
        switchedSections = await clearOtherGiftChoice(button, 'discovery');
        var formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', 1);
        formData.append('properties[_sample]', 'true');
        formData.append('sections', cartSections());
        formData.append('sections_url', window.location.pathname);
        res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
      }
      var data = await res.json();
      if (res.ok) renderCartSections(data.sections); // replaces the chooser with fresh state
      else if (switchedSections) renderCartSections(switchedSections);
    } catch (err) {
      if (switchedSections) renderCartSections(switchedSections);
    } finally {
      giftMutationPending = false;
      if (button.isConnected) button.removeAttribute('data-loading');
    }
  }

  document.addEventListener('click', (e) => {
    var toggle = e.target.closest('[data-sample-toggle]');
    if (toggle) toggleSample(toggle);
  });

  /* ----------------------------------------------------------------------
   * Free discovery set — same toggle pattern as complimentary samples above,
   * with a private `_discovery` line-item property instead of `_sample`.
   * -------------------------------------------------------------------- */
  async function toggleDiscovery(button) {
    if (giftMutationPending) return;
    giftMutationPending = true;
    button.setAttribute('data-loading', '');
    var switchedSections = null;
    try {
      var res;
      if (button.hasAttribute('data-discovery-remove')) {
        res = await fetch(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: button.dataset.lineKey,
            quantity: 0,
            sections: cartSections(),
            sections_url: window.location.pathname
          })
        });
      } else {
        switchedSections = await clearOtherGiftChoice(button, 'sample');
        var formData = new FormData();
        formData.append('id', button.dataset.variantId);
        formData.append('quantity', 1);
        formData.append('properties[_discovery]', 'true');
        formData.append('sections', cartSections());
        formData.append('sections_url', window.location.pathname);
        res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
      }
      var data = await res.json();
      if (res.ok) renderCartSections(data.sections); // replaces the chooser with fresh state
      else if (switchedSections) renderCartSections(switchedSections);
    } catch (err) {
      if (switchedSections) renderCartSections(switchedSections);
    } finally {
      giftMutationPending = false;
      if (button.isConnected) button.removeAttribute('data-loading');
    }
  }

  document.addEventListener('click', (e) => {
    var toggle = e.target.closest('[data-discovery-toggle]');
    if (toggle) toggleDiscovery(toggle);
  });

  /* ----------------------------------------------------------------------
   * Free gift box — once the cart already holds 2 eligible items (any 2
   * perfumes, or 1 perfume + 1 candle; never 2 candles), `cart-line-item.liquid`
   * shows a "Make it Luxury" trigger below each of the 2 matching rows; the
   * delegated handler below opens the confirmation dialog `cart-gift-box.liquid`
   * rendered with that pair. Confirming adds the free gift-box product
   * referencing both variant ids via line-item properties;
   * `cart-gift-box-card.liquid` reads those back off the gift-box line to
   * wrap the 2 paired rows in their own card.
   * -------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    var opener = e.target.closest('[data-gift-box-open]');
    if (!opener) return;
    var scope = opener.closest('[data-drawer], [data-cart-page]') || document;
    var dialog = scope.querySelector('[data-gift-box-dialog]');
    if (dialog) dialog.showModal();
  });

  class GiftBoxMaker extends HTMLElement {
    connectedCallback() {
      var self = this;
      this.dialog = this.querySelector('[data-gift-box-dialog]');
      this.confirmButton = this.querySelector('[data-gift-box-confirm]');
      this.confirmLabel = this.querySelector('[data-gift-box-confirm-label]');
      this.errorEl = this.querySelector('[data-gift-box-error]');
      this.options = Array.prototype.slice.call(this.querySelectorAll('[data-gift-box-option]'));

      this.addEventListener('change', function (e) {
        if (e.target.closest('[data-gift-box-option]')) self.syncOptions();
      });
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-gift-box-confirm]')) self.addToCart();
        else if (e.target.closest('[data-gift-box-cancel]')) self.dialog.close();
      });
      // Close on backdrop click (native <dialog> only closes on Escape by default).
      this.dialog.addEventListener('click', function (e) {
        if (e.target === self.dialog) self.dialog.close();
      });

      this.syncOptions();
    }

    // Caps selection at 2 and forbids 2 candles together: once 2 items (or 1
    // candle) are checked, every other still-unchecked option disables until
    // the shopper backs one out. Also drives the Confirm button's enabled state.
    syncOptions() {
      var checked = this.options.filter(function (o) {
        return o.checked;
      });
      var checkedCandles = checked.filter(function (o) {
        return o.dataset.category === 'candle';
      });
      this.options.forEach(function (o) {
        if (o.checked) return;
        var full = checked.length >= 2;
        var secondCandle = o.dataset.category === 'candle' && checkedCandles.length >= 1;
        o.disabled = full || secondCandle;
      });
      if (this.confirmButton) this.confirmButton.disabled = checked.length !== 2;
    }

    async addToCart() {
      if (this.confirmButton.disabled) return;
      var checked = this.options.filter(function (o) {
        return o.checked;
      });
      if (checked.length !== 2) return;

      this.confirmButton.disabled = true;
      this.confirmButton.setAttribute('aria-busy', '');
      this.confirmLabel.textContent = this.confirmButton.dataset.confirmingLabel;
      if (this.errorEl) this.errorEl.textContent = '';

      try {
        var res = await fetch(root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: this.dataset.giftBoxVariantId,
            quantity: 1,
            properties: {
              _gift_box: 'true',
              _gift_box_item_1: checked[0].value,
              _gift_box_item_2: checked[1].value
            },
            sections: cartSections(),
            sections_url: window.location.pathname
          })
        });
        var data = await res.json();
        if (!res.ok) {
          if (this.errorEl) this.errorEl.textContent = data.description || data.message || this.errorEl.dataset.errorLabel;
          return;
        }
        if (this.dialog.open) this.dialog.close();
        renderCartSections(data.sections); // re-render replaces this element with the fresh (now hidden) state
      } catch (err) {
        if (this.errorEl) this.errorEl.textContent = this.errorEl.dataset.errorLabel;
      } finally {
        if (this.isConnected) {
          this.confirmButton.removeAttribute('aria-busy');
          this.confirmLabel.textContent = this.confirmButton.dataset.confirmLabel;
          this.confirmButton.disabled = checked.length !== 2;
        }
      }
    }
  }
  if (!customElements.get('gift-box-maker')) customElements.define('gift-box-maker', GiftBoxMaker);

  // Catch gifts that were already stale on the initial server render (e.g. a
  // shopper reloads /cart after removing another item elsewhere).
  checkStaleGifts(document);

  /* ----------------------------------------------------------------------
   * Variant picker — client-side matching, no reload
   * -------------------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      var json = this.querySelector('[data-variants]');
      try {
        this.variants = JSON.parse(json ? json.textContent : '[]');
      } catch (err) {
        this.variants = [];
      }
      this.addEventListener('change', () => this.onChange());
    }
    selectedValues() {
      var radios = Array.prototype.slice.call(this.querySelectorAll('[data-option-selector]:checked'));
      radios.sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
      return radios.map((r) => r.value);
    }
    onChange() {
      var values = this.selectedValues();
      var match = this.variants.find(
        (v) => v.options.length === values.length && v.options.every((o, i) => o === values[i])
      );
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant: match } }));
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  function renderPrice(priceEl, variant) {
    var current = document.createElement('span');
    current.textContent = variant.price;
    var children = [current];
    if (variant.compare_at_price && variant.compare_amount > variant.price_amount) {
      var compare = document.createElement('s');
      compare.className = 'text-muted';
      compare.textContent = variant.compare_at_price;
      children.push(document.createTextNode(' '), compare);
    }
    priceEl.replaceChildren.apply(priceEl, children);
  }

  document.addEventListener('variant:change', (e) => {
    var variant = e.detail.variant;
    var scope = e.target.closest('[data-product]') || document;
    var idInputs = scope.querySelectorAll('[data-variant-id]');
    var priceEls = scope.querySelectorAll('[data-price]');
    var addBtns = scope.querySelectorAll('[data-add-to-cart]');
    var addLabels = scope.querySelectorAll('[data-add-label]');
    var strings = {};
    try {
      strings = JSON.parse(scope.dataset.strings || '{}');
    } catch (err) {}

    if (!variant) {
      addBtns.forEach((b) => (b.disabled = true));
      addLabels.forEach((l) => (l.textContent = strings.unavailable || ''));
      return;
    }

    idInputs.forEach((i) => (i.value = variant.id));
    priceEls.forEach((el) => renderPrice(el, variant));
    addBtns.forEach((b) => (b.disabled = !variant.available));
    addLabels.forEach((l) => (l.textContent = variant.available ? strings.add || l.textContent : strings.soldOut || ''));

    var gallery = scope.querySelector('product-gallery');
    if (gallery && gallery.showMedia && variant.media_id) gallery.showMedia(variant.media_id);

    if (window.history && history.replaceState) {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  });

  /* ----------------------------------------------------------------------
   * Product gallery — swipeable Swiper slider + thumbnail rail + variant sync.
   * Swiper is a deferred CDN script that may load after this file, so init is
   * retried on window load; the CSS shows the first slide until then.
   * -------------------------------------------------------------------- */
  var PSWP_VERSION = 'photoswipe@5';

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-gallery-main]');
      this.addEventListener('click', (e) => {
        var thumb = e.target.closest('[data-thumb]');
        if (thumb) this.goTo(Number(thumb.dataset.index));
      });
      if (!this.initSlider()) window.addEventListener('load', () => this.initSlider());
      this.initZoom();
    }
    initZoom() {
      if (!this.querySelector('[data-pswp-width]')) return;
      var self = this;
      import('https://cdn.jsdelivr.net/npm/' + PSWP_VERSION + '/dist/photoswipe-lightbox.esm.min.js').then(function (mod) {
        var PhotoSwipeLightbox = mod.default;
        var strings = {};
        try {
          strings = JSON.parse(self.dataset.pswpStrings || '{}');
        } catch (err) {
          strings = {};
        }
        var lightbox = new PhotoSwipeLightbox({
          gallery: self,
          children: '[data-pswp-width]',
          pswpModule: function () {
            return import('https://cdn.jsdelivr.net/npm/' + PSWP_VERSION + '/dist/photoswipe.esm.min.js');
          },
          bgOpacity: 0.7,
          showHideAnimationType: 'zoom',
          closeTitle: strings.close,
          arrowPrevTitle: strings.prev,
          arrowNextTitle: strings.next,
        });
        self.registerZoomControls(lightbox, strings);
        lightbox.init();
      });
    }
    registerZoomControls(lightbox, strings) {
      lightbox.addFilter('uiElement', function (element, data) {
        if (data.name === 'close') {
          element.innerHTML =
            '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 5l14 14M19 5L5 19" stroke-linecap="round"/></svg>';
        }
        return element;
      });

      lightbox.on('uiRegister', function () {
        var pswp = lightbox.pswp;
        function centerPoint() {
          return { x: pswp.viewportSize.x / 2, y: pswp.viewportSize.y / 2 };
        }
        function zoomBy(factor) {
          var levels = pswp.currSlide.zoomLevels;
          var next = pswp.currSlide.currZoomLevel * factor;
          next = Math.min(Math.max(next, levels.min), levels.max);
          pswp.zoomTo(next, centerPoint(), 233);
        }
        pswp.ui.registerElement({
          name: 'zoom-reset',
          ariaLabel: strings.zoomReset,
          order: 7,
          isButton: true,
          html: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M20 11A8 8 0 1 0 18.5 16" stroke-linecap="round"/><path d="M20 5v6h-6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
          onClick: function () {
            pswp.zoomTo(pswp.currSlide.zoomLevels.initial, centerPoint(), 233);
          },
        });
        pswp.ui.registerElement({
          name: 'zoom-out',
          ariaLabel: strings.zoomOut,
          order: 8,
          isButton: true,
          html: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.35-4.35M7.5 10.5h6" stroke-linecap="round"/></svg>',
          onClick: function () {
            zoomBy(1 / 1.5);
          },
        });
        pswp.ui.registerElement({
          name: 'zoom-in',
          ariaLabel: strings.zoomIn,
          order: 9,
          isButton: true,
          html: '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.6"><circle cx="10.5" cy="10.5" r="6.5"/><path d="M20 20l-4.35-4.35M10.5 7.5v6M7.5 10.5h6" stroke-linecap="round"/></svg>',
          onClick: function () {
            zoomBy(1.5);
          },
        });
      });
    }
    initSlider() {
      if (!this.stage) return true; // nothing to slide (0–1 media)
      if (this.stage.swiper) return true; // already initialised
      if (!window.Swiper) return false; // Swiper not loaded yet — retry later
      var self = this;
      new window.Swiper(this.stage, {
        slidesPerView: 1,
        speed: 500,
        grabCursor: true,
        navigation: {
          nextEl: this.querySelector('[data-gallery-next]'),
          prevEl: this.querySelector('[data-gallery-prev]'),
        },
        on: {
          slideChange: function () {
            self.setActiveThumb(this.activeIndex);
          },
        },
      });
      this.setActiveThumb(this.stage.swiper.activeIndex);
      return true;
    }
    goTo(index) {
      if (this.stage && this.stage.swiper) this.stage.swiper.slideTo(index);
      else this.setActiveThumb(index);
    }
    setActiveThumb(index) {
      this.querySelectorAll('[data-thumb]').forEach((t) => {
        t.toggleAttribute('data-active', Number(t.dataset.index) === Number(index));
      });
    }
    showMedia(id) {
      if (!this.stage) return;
      var slides = this.stage.querySelectorAll('.swiper-slide');
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.mediaId === String(id)) {
          this.goTo(i);
          return;
        }
      }
    }
  }
  if (!customElements.get('product-gallery')) customElements.define('product-gallery', ProductGallery);

  /* ----------------------------------------------------------------------
   * Hero video play/pause — toggles the autoplaying background <video> in the
   * same section and mirrors state to [data-paused] (CSS swaps the icon) and
   * the button's aria-label.
   * -------------------------------------------------------------------- */
  class VideoToggle extends HTMLElement {
    connectedCallback() {
      var container = this.closest('[data-video-container]') || this.closest('section');
      this.video = container && container.querySelector('video');
      this.button = this.querySelector('[data-video-toggle]');
      this.muteButton = this.querySelector('[data-video-mute]');
      if (!this.video || !this.button) return;

      this.button.addEventListener('click', () => {
        if (this.video.paused) this.video.play();
        else {
          this.video.pause();
          if (this.button.hasAttribute('data-video-stop')) this.video.currentTime = 0;
        }
      });

      if (this.muteButton) {
        this.muteButton.addEventListener('click', () => {
          this.video.muted = !this.video.muted;
          this._syncAudio();
        });
      }

      this._sync = this._sync.bind(this);
      this.video.addEventListener('play', this._sync);
      this.video.addEventListener('pause', this._sync);
      this._sync();
      this._syncAudio();
    }
    disconnectedCallback() {
      if (!this.video) return;
      this.video.removeEventListener('play', this._sync);
      this.video.removeEventListener('pause', this._sync);
    }
    _sync() {
      var paused = this.video.paused;
      this.toggleAttribute('data-paused', paused);
      var label = this.button.getAttribute(paused ? 'data-label-play' : 'data-label-pause');
      if (label) this.button.setAttribute('aria-label', label);
    }
    _syncAudio() {
      if (!this.muteButton || !this.video) return;
      var muted = this.video.muted;
      this.toggleAttribute('data-muted', muted);
      var label = this.muteButton.getAttribute(muted ? 'data-label-unmute' : 'data-label-mute');
      if (label) this.muteButton.setAttribute('aria-label', label);
      // Toggle icon visibility
      var mutedIcon = this.muteButton.querySelector('.video-toggle__vol-muted');
      var unmutedIcon = this.muteButton.querySelector('.video-toggle__vol-unmuted');
      if (mutedIcon) mutedIcon.hidden = !muted;
      if (unmutedIcon) unmutedIcon.hidden = muted;
    }
  }
  if (!customElements.get('video-toggle')) customElements.define('video-toggle', VideoToggle);

  /* ----------------------------------------------------------------------
   * Quantity steppers (PDP form)
   * -------------------------------------------------------------------- */
  document.addEventListener('click', (e) => {
    var wrap = e.target.closest('[data-qty]');
    if (!wrap) return;
    var input = wrap.querySelector('input');
    if (!input) return;
    var value = parseInt(input.value, 10) || 1;
    if (e.target.closest('[data-qty-plus]')) input.value = value + 1;
    else if (e.target.closest('[data-qty-minus]')) input.value = Math.max(1, value - 1);
  });

  /* ----------------------------------------------------------------------
   * Read-more — reveal the toggle only when copy actually overflows the clamp,
   * then flip [data-expanded] (CSS handles the clamp + label swap).
   * -------------------------------------------------------------------- */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector('[data-read-more-text]');
      this.toggle = this.querySelector('[data-read-more-toggle]');
      if (!this.text || !this.toggle) return;

      this.toggle.addEventListener('click', () => {
        var expanded = this.toggleAttribute('data-expanded');
        this.toggle.setAttribute('aria-expanded', expanded);
      });

      this._sync = this._sync.bind(this);
      this.resizeObserver = new ResizeObserver(this._sync);
      this.resizeObserver.observe(this.text);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    // Hide the toggle when the copy fits within the clamp; only meaningful while collapsed.
    _sync() {
      if (this.hasAttribute('data-expanded')) return;
      this.toggle.hidden = this.text.scrollHeight <= this.text.clientHeight + 1;
    }
  }
  if (!customElements.get('read-more')) customElements.define('read-more', ReadMore);

  /* ----------------------------------------------------------------------
   * Collection product list — infinite scroll + shareable ?page= URL
   *
   * Appends the next page via the Section Rendering API as the footer nears
   * the viewport, and mirrors the page currently in view into the address
   * bar so the listing can be reloaded or shared from where you left off.
   * Falls back to numbered links without JS.
   * -------------------------------------------------------------------- */
  class ProductList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-product-grid]');
      this.footer = this.querySelector('[data-product-list-footer]');
      this.spinner = this.querySelector('[data-product-list-spinner]');
      this.pagination = this.querySelector('[data-product-list-pagination]');
      this.sectionId = this.dataset.sectionId;
      this.loading = false;

      var nextLink = this.querySelector('[data-product-list-next]');
      this.nextUrl = nextLink ? nextLink.getAttribute('href') : null;
      this.currentPage = (this.grid && this.grid.dataset.page) || '1';

      // Single page of results — nothing to enhance.
      if (!this.nextUrl && this.currentPage === '1') return;

      // Infinite scroll supersedes the numbered control (kept for no-JS).
      if (this.pagination) this.pagination.hidden = true;

      if (this.grid && this.grid.firstElementChild) {
        this.grid.firstElementChild.setAttribute('data-page-start', this.currentPage);
      }
      this._initPageObserver();
      this._initLoadObserver();
    }

    disconnectedCallback() {
      if (this.loadObserver) this.loadObserver.disconnect();
      if (this.pageObserver) this.pageObserver.disconnect();
    }

    // Reflect the page nearest the top of the viewport in the address bar.
    _initPageObserver() {
      this.pageObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) this._setPage(entry.target.dataset.pageStart);
          });
        },
        { rootMargin: '0px 0px -85% 0px' }
      );
      this.querySelectorAll('[data-page-start]').forEach((m) => this.pageObserver.observe(m));
    }

    _setPage(page) {
      if (!page || page === this.currentPage) return;
      this.currentPage = page;
      if (!(window.history && history.replaceState)) return;
      var url = new URL(window.location.href);
      if (page === '1') url.searchParams.delete('page');
      else url.searchParams.set('page', page);
      history.replaceState(history.state, '', url);
    }

    // Pull in the next page once the footer nears the viewport.
    _initLoadObserver() {
      if (!this.footer || !this.nextUrl) return;
      this.loadObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) this._loadNext();
        },
        { rootMargin: '600px 0px' }
      );
      this.loadObserver.observe(this.footer);
    }

    async _loadNext() {
      if (this.loading || !this.nextUrl) return;
      this.loading = true;
      if (this.spinner) this.spinner.hidden = false;

      try {
        var url = new URL(this.nextUrl, window.location.origin);
        var page = url.searchParams.get('page') || '';
        url.searchParams.set('section_id', this.sectionId);

        var res = await fetch(url.toString());
        var doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        var freshGrid = doc.querySelector('[data-product-grid]');
        var cards = freshGrid ? Array.prototype.slice.call(freshGrid.children) : [];
        var nextLink = doc.querySelector('[data-product-list-next]');

        cards.forEach((card, i) => {
          var node = document.importNode(card, true);
          if (i === 0) {
            node.setAttribute('data-page-start', page);
            this.pageObserver.observe(node);
          }
          this.grid.appendChild(node);
        });

        this.nextUrl = cards.length && nextLink ? nextLink.getAttribute('href') : null;
      } catch (err) {
        // Restore the numbered links so the catalogue stays reachable.
        if (this.pagination) this.pagination.hidden = false;
        this.nextUrl = null;
      } finally {
        this.loading = false;
        if (this.spinner) this.spinner.hidden = true;
      }

      if (!this.nextUrl) {
        if (this.loadObserver) this.loadObserver.disconnect();
        if (this.footer) this.footer.hidden = true;
        return;
      }
      // Short pages may leave the footer still in view — re-evaluate to keep loading.
      if (this.loadObserver) {
        this.loadObserver.unobserve(this.footer);
        this.loadObserver.observe(this.footer);
      }
    }
    // Re-reads the live DOM state and re-attaches the load observer.
    // Called by FilterDrawer after a filter/sort AJAX swap.
    reinit() {
      if (this.loadObserver) this.loadObserver.disconnect();
      if (this.pageObserver) this.pageObserver.disconnect();

      this.grid = this.querySelector('[data-product-grid]');
      this.footer = this.querySelector('[data-product-list-footer]');
      this.spinner = this.querySelector('[data-product-list-spinner]');
      this.pagination = this.querySelector('[data-product-list-pagination]');
      this.loading = false;

      var nextLink = this.querySelector('[data-product-list-next]');
      this.nextUrl = nextLink ? nextLink.getAttribute('href') : null;
      this.currentPage = (this.grid && this.grid.dataset.page) || '1';

      if (this.pagination) this.pagination.hidden = !!this.nextUrl;
      if (this.grid && this.grid.firstElementChild) {
        this.grid.firstElementChild.setAttribute('data-page-start', this.currentPage);
      }
      this._initPageObserver();
      this._initLoadObserver();
    }
  }
  if (!customElements.get('product-list')) customElements.define('product-list', ProductList);

  /* ----------------------------------------------------------------------
   * Sticky add-to-cart bar
   * -------------------------------------------------------------------- */
  function initStickyAtc(scope) {
    (scope || document).querySelectorAll('[data-sticky-atc]').forEach((bar) => {
      var anchor = document.querySelector('[data-atc-anchor]');
      if (!anchor || bar.dataset.bound) return;
      bar.dataset.bound = '1';
      var io = new IntersectionObserver(
        ([entry]) => {
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.toggleAttribute('data-visible', scrolledPast);
        },
        { threshold: 0 }
      );
      io.observe(anchor);
    });
  }

  function initHeader() {
    var header = document.querySelector('header[data-header]');
    if (!header) return;
    if (header.dataset.bound) return;
    header.dataset.bound = '1';

    var shell = document.querySelector('.page-shell');
    var firstSection = document.querySelector('.page-shell > .shopify-section:first-child');
    var media = firstSection ? Array.prototype.slice.call(firstSection.querySelectorAll('img, video')) : [];
    var averageColor;
    var updateContrast = debounce(function () {
      if (!averageColor || header.hasAttribute('data-scrolled')) return;

      var headerRect = header.getBoundingClientRect();
      var bestMedia;
      var bestOverlap = 0;
      media.forEach(function (item) {
        var rect = item.getBoundingClientRect();
        var overlapWidth = Math.max(0, Math.min(headerRect.right, rect.right) - Math.max(headerRect.left, rect.left));
        var overlapHeight = Math.max(0, Math.min(headerRect.bottom, rect.bottom) - Math.max(headerRect.top, rect.top));
        var overlap = overlapWidth * overlapHeight;
        if (overlap > bestOverlap) {
          bestOverlap = overlap;
          bestMedia = item;
        }
      });

      if (!bestMedia) {
        header.dataset.textColor = 'black';
        return;
      }

      var options = getMediaSampleOptions(bestMedia, header);
      if (!options) return;
      var color = averageColor.getColor(bestMedia, options);
      if (!color.error) header.dataset.textColor = color.isDark ? 'white' : 'black';
    }, 100);

    media.forEach(function (item) {
      item.addEventListener('load', updateContrast);
      item.addEventListener('loadeddata', updateContrast);
    });
    if (firstSection) {
      var contrastObserver = new ResizeObserver(updateContrast);
      contrastObserver.observe(header);
      contrastObserver.observe(firstSection);
    }
    if (media.some((item) => item.tagName === 'VIDEO')) {
      window.setInterval(function () {
        if (!document.hidden && !header.hasAttribute('data-scrolled')) updateContrast();
      }, 1000);
    }

    loadFastAverageColor()
      .then(function (module) {
        averageColor = new module.FastAverageColor();
        updateContrast();
      })
      .catch(function () {});

    var onScroll = function () {
      var scrolled = window.scrollY > 8;
      header.toggleAttribute('data-scrolled', scrolled);
      var atFooter = scrolled && shell && shell.getBoundingClientRect().bottom <= window.innerHeight;
      header.toggleAttribute('data-hidden', atFooter);
      if (!scrolled) updateContrast();
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ----------------------------------------------------------------------
   * Lazy videos — swap in the real src (and start playback) only once the
   * <video> nears the viewport, so off-screen reels don't fetch until needed.
   * -------------------------------------------------------------------- */
  function initLazyVideos(scope) {
    var els = (scope || document).querySelectorAll('[data-lazy-video]:not([src])');
    if (!els.length) return;
    var io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          var video = entry.target;
          video.src = video.dataset.src;
          video.load();
          if (!video.closest('[data-video-slider]')) {
            video.autoplay = true;
            video.play().catch(() => {});
          }
          io.unobserve(video);
        });
      },
      { rootMargin: '200px 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  function initVisibleSliderVideos(scope) {
    var sliders = (scope || document).querySelectorAll('[data-video-slider]:not([data-video-visibility-ready])');
    sliders.forEach((slider) => {
      slider.setAttribute('data-video-visibility-ready', '');
      var videos = slider.querySelectorAll('video');
      var io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            var video = entry.target;
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              if (!video.src && video.dataset.src) {
                video.src = video.dataset.src;
                video.load();
              }
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });
        },
        { threshold: [0, 0.6] }
      );
      videos.forEach((video) => io.observe(video));
    });
  }

  /* ----------------------------------------------------------------------
   * Carousels (Swiper, loaded per-section via CDN)
   * -------------------------------------------------------------------- */
  function initSwipers(scope) {
    if (!window.Swiper) return;
    (scope || document).querySelectorAll('[data-swiper]').forEach((el) => {
      if (el.swiper) return;
      var opts = {};
      try {
        opts = JSON.parse(el.dataset.swiperOptions || '{}');
      } catch (err) {}
      if (prefersReducedMotion) opts.autoplay = false;
      new window.Swiper(el, opts);
    });
  }

  /* ----------------------------------------------------------------------
   * Scroll reveals (CSS handles the transition; JS toggles .is-visible)
   * -------------------------------------------------------------------- */
  function initReveals(scope) {
    var els = (scope || document).querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!els.length) return;
    if (prefersReducedMotion) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    var io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    );
    els.forEach((el) => io.observe(el));
  }

  /* ----------------------------------------------------------------------
   * Product scroller — arrow controls for a horizontal scroll-snap row
   * (e.g. the search drawer). Self-binds via connectedCallback, so it works
   * on first render and after shopify:section:load.
   * -------------------------------------------------------------------- */
  class ProductScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller-track]');
      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      if (!this.track) return;
      if (this.prev) this.prev.addEventListener('click', () => this.page(-1));
      if (this.next) this.next.addEventListener('click', () => this.page(1));
      this._onScroll = () => this.updateArrows();
      this.track.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onScroll, { passive: true });
      this.updateArrows();
    }
    disconnectedCallback() {
      window.removeEventListener('resize', this._onScroll);
    }
    page(dir) {
      this.track.scrollBy({ left: this.track.clientWidth * 0.8 * dir, behavior: 'smooth' });
    }
    updateArrows() {
      var max = this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prev) this.prev.toggleAttribute('data-hidden', this.track.scrollLeft <= 0);
      if (this.next) this.next.toggleAttribute('data-hidden', this.track.scrollLeft >= max);
    }
  }
  if (!customElements.get('product-scroller')) customElements.define('product-scroller', ProductScroller);

  class InspirationContrast extends HTMLElement {
    connectedCallback() {
      this.section = this.querySelector('[data-inspiration-section]');
      this.copy = this.querySelector('[data-inspiration-copy]');
      this.images = Array.prototype.slice.call(this.querySelectorAll('[data-inspiration-image]'));
      if (!this.section || !this.copy || !this.images.length) return;

      this._update = debounce(() => this.update(), 100);
      this._observer = new ResizeObserver(this._update);
      this._observer.observe(this.section);
      this._observer.observe(this.copy);
      this.images.forEach((image) => image.addEventListener('load', this._update));

      loadFastAverageColor()
        .then((module) => {
          if (!this.isConnected) return;
          this.averageColor = new module.FastAverageColor();
          this.update();
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._update) this.images.forEach((image) => image.removeEventListener('load', this._update));
      if (this.averageColor) this.averageColor.destroy();
    }

    update() {
      if (!this.averageColor) return;
      var image = this.images.find((item) => item.offsetWidth && item.offsetHeight);
      if (!image || !image.complete || !image.naturalWidth) return;

      var options = getMediaSampleOptions(image, this.copy);
      if (!options) return;
      var color = this.averageColor.getColor(image, options);
      if (!color.error) this.dataset.textColor = color.isDark ? 'white' : 'black';
    }
  }
  if (!customElements.get('inspiration-contrast')) customElements.define('inspiration-contrast', InspirationContrast);

  /* ----------------------------------------------------------------------
   * Statement image trail — on desktop, product images from a collection
   * follow the cursor across the Statement section, cycling round-robin and
   * fading out. Recycles a fixed pool of nodes (no DOM churn). Self-binds, so
   * it re-arms after shopify:section:load. Disabled on touch / coarse pointers
   * and when the visitor prefers reduced motion.
   * -------------------------------------------------------------------- */
  class StatementTrail extends HTMLElement {
    connectedCallback() {
      this.items = Array.prototype.slice.call(this.querySelectorAll('[data-trail-img]'));
      if (!this.items.length) return;
      var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (!fine || prefersReducedMotion) return;

      this.i = 0;
      this.last = null;
      this.threshold = 260; // px of cursor travel before the next product image appears
      this.timers = new WeakMap();
      this.frame = null;
      this._onMove = this.onMove.bind(this);
      this.addEventListener('pointermove', this._onMove, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this._onMove);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    // Coalesce moves to one spawn attempt per frame, always using the latest coords.
    onMove(e) {
      this.cx = e.clientX;
      this.cy = e.clientY;
      if (this.frame) return;
      var self = this;
      this.frame = requestAnimationFrame(function () {
        self.frame = null;
        self.spawn();
      });
    }

    spawn() {
      var rect = this.getBoundingClientRect();
      var x = this.cx - rect.left;
      var y = this.cy - rect.top;
      if (this.last) {
        var dx = x - this.last.x;
        var dy = y - this.last.y;
        if (dx * dx + dy * dy < this.threshold * this.threshold) return;
      }
      this.last = { x: x, y: y };

      var el = this.items[this.i];
      this.i = (this.i + 1) % this.items.length;
      el.style.setProperty('--x', x + 'px');
      el.style.setProperty('--y', y + 'px');
      el.setAttribute('data-active', '');

      var prev = this.timers.get(el);
      if (prev) clearTimeout(prev);
      this.timers.set(el, setTimeout(function () {
        el.removeAttribute('data-active'); // hold the image at full opacity, then trigger the CSS fade-out
      }, 1200));
    }
  }
  if (!customElements.get('statement-trail')) customElements.define('statement-trail', StatementTrail);

  var saltMotionPromise;

  function loadSaltMotion() {
    if (saltMotionPromise) return saltMotionPromise;
    function load(src, integrity) {
      return new Promise(function (resolve, reject) {
        var script = document.createElement('script');
        script.src = src;
        script.integrity = integrity;
        script.crossOrigin = 'anonymous';
        script.onload = resolve;
        script.onerror = reject;
        document.head.appendChild(script);
      });
    }
    saltMotionPromise = (window.gsap ? Promise.resolve() : load(
      'https://cdn.jsdelivr.net/npm/gsap@3.15.0/dist/gsap.min.js',
      'sha384-XmJ9SoHtVOHoQUcKvFAzVXwdkKo1Ie3bhmSoIAkcdsHGaIrVJIkmozyq0FJeb/Ly'
    )).then(function () {
      return window.ScrollTrigger ? undefined : load(
        'https://cdn.jsdelivr.net/npm/gsap@3.15.0/dist/ScrollTrigger.min.js',
        'sha384-wl5TeDVvOWt30Pbf8aSo2ZrzsOjddu3avOBvHe+p+OhJt9gP6w9YXmDkN5DK2/dF'
      );
    });
    return saltMotionPromise;
  }

  class SaltKissedPage extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      var signal = this.controller.signal;
      this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.videos = Array.from(this.querySelectorAll('video'));
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.videos.forEach((video) => video.pause());
      }, { signal: signal });
      this.motionPreference.addEventListener('change', () => this.startMotion(signal), { signal: signal });
      this.startMotion(signal);
    }

    startMotion(signal) {
      if (this.motionPreference.matches || this.motion || this.motionLoading) return;
      this.motionLoading = true;
      loadSaltMotion().then(() => {
        if (signal.aborted || !this.isConnected) return;
        var gsap = window.gsap;
        gsap.registerPlugin(window.ScrollTrigger);
        this.motion = gsap.matchMedia();
        this.motion.add('(prefers-reduced-motion: no-preference)', () => {
          var reveal = (element, delay) => {
            var bounds = element.getBoundingClientRect();
            if (!bounds.height || bounds.top < window.innerHeight * 0.94) return;
            gsap.from(element, {
              y: 20, opacity: element.dataset.saltReveal === 'control' ? 1 : 0,
              duration: 0.75, delay: delay || 0, ease: 'power3.out', clearProps: 'transform,opacity',
              scrollTrigger: { trigger: element, start: 'top 94%', once: true }
            });
          };
          this.querySelectorAll('[data-salt-reveal]').forEach((element) => {
            reveal(element);
          });
          this.querySelectorAll('[data-salt-stagger]').forEach((group) => {
            var columns = group.classList.contains('salt-note__list') ? 3 : 2;
            Array.from(group.children).forEach((element, index) => reveal(element, (index % columns) * 0.08));
          });
          this.querySelectorAll('.salt-feeling__fruit > .salt-editorial-image, .salt-feeling__skin > .salt-editorial-image, .salt-perfumer__aside img').forEach((element) => {
            if (element.getBoundingClientRect().top < window.innerHeight * 0.94) return;
            gsap.from(element, {
              scale: 1.035, y: 16, duration: 1.1, ease: 'power3.out', clearProps: 'transform',
              scrollTrigger: { trigger: element, start: 'top 94%', once: true }
            });
          });
          var onFocus = (event) => {
            var element = event.target.closest('[data-salt-reveal]');
            if (element) gsap.getTweensOf(element).forEach((tween) => tween.progress(1));
          };
          this.addEventListener('focusin', onFocus);
          return () => this.removeEventListener('focusin', onFocus);
        }, this);
        this.motion.add('(min-width: 1024px) and (prefers-reduced-motion: no-preference)', () => {
          this.querySelectorAll('[data-salt-drift]').forEach((element) => {
            gsap.fromTo(element, { y: 12 }, {
              y: -12, ease: 'none',
              scrollTrigger: { trigger: element.parentElement, start: 'top bottom', end: 'bottom top', scrub: true }
            });
          });
        }, this);
      }).catch(() => {}).finally(() => {
        if (this.controller.signal === signal) this.motionLoading = false;
      });
      this.startPointerMotion();
    }

    startPointerMotion() {
      if (this.pointerMotion) return;
      this.pointerMotion = window.matchMedia('(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)');
      var setup = () => {
        this.stopPointerMotion?.();
        if (!this.pointerMotion.matches) return;
        var controller = new AbortController();
        var active, frame, clientX, clientY;
        var reset = () => {
          cancelAnimationFrame(frame);
          frame = null;
          if (!active) return;
          active.removeAttribute('data-salt-hover-active');
          active.style.removeProperty('--salt-pointer-x');
          active.style.removeProperty('--salt-pointer-y');
          active = null;
        };
        this.addEventListener('pointermove', (event) => {
          if (event.pointerType !== 'mouse') return;
          var target = event.target.closest('[data-salt-hover]');
          if (target !== active) reset();
          if (!target) return;
          active = target;
          clientX = event.clientX;
          clientY = event.clientY;
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            var bounds = active.getBoundingClientRect();
            if (!bounds.width || !bounds.height) return;
            var x = Math.max(-1, Math.min(1, (clientX - bounds.left) / bounds.width * 2 - 1));
            var y = Math.max(-1, Math.min(1, (clientY - bounds.top) / bounds.height * 2 - 1));
            active.style.setProperty('--salt-pointer-x', (x * 3) + 'px');
            active.style.setProperty('--salt-pointer-y', (y * 3) + 'px');
            active.setAttribute('data-salt-hover-active', '');
          });
        }, { signal: controller.signal, passive: true });
        this.addEventListener('pointerout', (event) => {
          if (active && !active.contains(event.relatedTarget)) reset();
        }, { signal: controller.signal, passive: true });
        this.stopPointerMotion = () => { controller.abort(); reset(); };
      };
      this.pointerMotion.addEventListener('change', setup, { signal: this.controller.signal });
      setup();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.videos?.forEach((video) => video.pause());
      this.motion?.revert();
      this.stopPointerMotion?.();
      this.pointerMotion = null;
      this.motion = null;
      this.motionLoading = false;
    }
  }
  if (!customElements.get('salt-kissed-page')) customElements.define('salt-kissed-page', SaltKissedPage);

  /* ----------------------------------------------------------------------
   * Boot
   * -------------------------------------------------------------------- */
  function init(scope) {
    initReveals(scope);
    initStickyAtc(scope);
    initHeader();
    initSwipers(scope);
    initLazyVideos(scope);
    initVisibleSliderVideos(scope);
  }

  if (document.readyState !== 'loading') init(document);
  else document.addEventListener('DOMContentLoaded', () => init(document));

  // PDP: the "Fragrance Notes" chip below the title opens the notes accordion
  // panel and smooth-scrolls it into view.
  document.addEventListener('click', (e) => {
    var chip = e.target.closest('[data-notes-jump]');
    if (!chip) return;
    var panel = document.getElementById(chip.getAttribute('data-target'));
    if (!panel) return;
    panel.open = true;
    requestAnimationFrame(() => panel.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  });

  // Swiper is a deferred CDN script that may execute after this file — retry on load
  window.addEventListener('load', () => initSwipers(document));

  // Theme editor: re-bind when a section is re-rendered
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
